import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["mini-tort"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      miniTortClaimList: build.query<
        MiniTortClaimListApiResponse,
        MiniTortClaimListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/`,
          params: {
            injury_detected: queryArg.injuryDetected,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
            stage: queryArg.stage,
          },
        }),
        transformResponse: (response: unknown): MiniTortClaimListApiResponse => {
          if (Array.isArray(response)) {
            const first = response[0] as MiniTortClaimListSchema | undefined;
            return first ?? { results: [], count: 0 };
          }
          return response as MiniTortClaimListSchema;
        },
        providesTags: ["mini-tort"],
      }),
      miniTortClaimRetrieve: build.query<
        MiniTortClaimRetrieveApiResponse,
        MiniTortClaimRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/mini-tort/claims/${queryArg.id}/` }),
        providesTags: ["mini-tort"],
      }),
      miniTortClaimPartialUpdate: build.mutation<
        MiniTortClaimPartialUpdateApiResponse,
        MiniTortClaimPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedMiniTortClaimUpdateSchema,
        }),
        invalidatesTags: ["mini-tort"],
      }),
      miniTortClaimAutoCloseHold: build.mutation<
        MiniTortClaimAutoCloseHoldApiResponse,
        MiniTortClaimAutoCloseHoldApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/auto-close/hold/`,
          method: "POST",
        }),
        invalidatesTags: ["mini-tort"],
      }),
      miniTortClaimAutoCloseResume: build.mutation<
        MiniTortClaimAutoCloseResumeApiResponse,
        MiniTortClaimAutoCloseResumeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/auto-close/resume/`,
          method: "POST",
        }),
        invalidatesTags: ["mini-tort"],
      }),
      miniTortClaimTimeline: build.query<
        MiniTortClaimTimelineApiResponse,
        MiniTortClaimTimelineApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/timeline/`,
        }),
        transformResponse: (response: unknown): MiniTortClaimTimelineApiResponse => {
          if (Array.isArray(response)) return response as MiniTortTimelineEntry[];
          if (response) return [response as MiniTortTimelineEntry];
          return [];
        },
        providesTags: ["mini-tort"],
      }),
      miniTortClaimTransition: build.mutation<
        MiniTortClaimTransitionApiResponse,
        MiniTortClaimTransitionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/transition/`,
          method: "POST",
          body: queryArg.miniTortStageTransitionRequest,
        }),
        invalidatesTags: ["mini-tort"],
      }),
      miniTortClaimFlags: build.query<
        MiniTortClaimFlagsApiResponse,
        MiniTortClaimFlagsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/flags/`,
        }),
        providesTags: ["mini-tort"],
      }),
      miniTortClaimFlagResolve: build.mutation<
        MiniTortClaimFlagResolveApiResponse,
        MiniTortClaimFlagResolveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.claimId}/flags/${queryArg.flagId}/resolve/`,
          method: "POST",
          body: { resolved_reason: queryArg.resolvedReason },
        }),
        invalidatesTags: ["mini-tort"],
      }),
      miniTortClaimStatus: build.query<
        MiniTortClaimStatusApiResponse,
        MiniTortClaimStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mini-tort/claims/${queryArg.id}/status/`,
        }),
        providesTags: ["mini-tort"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };

// ---------------------------------------------------------------------------
// API arg/response types
// ---------------------------------------------------------------------------

export type MiniTortClaimListApiResponse = MiniTortClaimListSchema;
export type MiniTortClaimListApiArg = {
  injuryDetected?: boolean;
  limit?: number;
  offset?: number;
  ordering?: string;
  stage?: string;
};
export type MiniTortClaimRetrieveApiResponse = MiniTortClaimDetailSchema;
export type MiniTortClaimRetrieveApiArg = { id: string };
export type MiniTortClaimPartialUpdateApiResponse = MiniTortClaimSchema;
export type MiniTortClaimPartialUpdateApiArg = {
  id: string;
  patchedMiniTortClaimUpdateSchema: MiniTortClaimUpdateSchema;
};
export type MiniTortClaimAutoCloseHoldApiResponse = MiniTortClaimSchema;
export type MiniTortClaimAutoCloseHoldApiArg = { id: string };
export type MiniTortClaimAutoCloseResumeApiResponse = MiniTortClaimSchema;
export type MiniTortClaimAutoCloseResumeApiArg = { id: string };
export type MiniTortClaimTimelineApiResponse = MiniTortTimelineEntry[];
export type MiniTortClaimTimelineApiArg = { id: string };
export type MiniTortClaimTransitionApiResponse = MiniTortClaimSchema;
export type MiniTortClaimTransitionApiArg = {
  id: string;
  miniTortStageTransitionRequest: MiniTortStageTransitionRequest;
};
export type MiniTortClaimFlagsApiResponse = MiniTortClaimFlagSchema[];
export type MiniTortClaimFlagsApiArg = { id: string };
export type MiniTortClaimFlagResolveApiResponse = MiniTortClaimFlagSchema;
export type MiniTortClaimFlagResolveApiArg = {
  claimId: string;
  flagId: string;
  resolvedReason: string;
};
export type MiniTortClaimStatusApiResponse = MiniTortPipelineStatus;
export type MiniTortClaimStatusApiArg = { id: string };

// ---------------------------------------------------------------------------
// Enums — must match backend app/defs/mini_tort/mini_tort.py exactly
// ---------------------------------------------------------------------------

export type MiniTortStage =
  | "processing_submission"
  | "reviewing_claim"
  | "need_additional_materials"
  | "retrieving_police_report"
  | "generating_demand"
  | "demand_submitted"
  | "insurer_acknowledged"
  | "payment_issued"
  | "payment_en_route"
  | "closed"
  | "rejected";

export type ContactMethod = "email" | "phone" | "text";

export type PrerequisiteStatus =
  | "pending"
  | "extracted"
  | "processed"
  | "complete"
  | "approved"
  | "failed"
  | "manual"
  | "waived";

export type MiniTortFlagSeverity = "blocker" | "warning" | "info";

export type FaultStatus = "yes" | "no" | "partially";
export type DeductibleStatus = "yes" | "no" | "not_yet";

export type MiniTortAttachmentType =
  | "police_report"
  | "insurance_dec_page"
  | "vehicle_damage_photo"
  | "other";

export type MiniTortAttachmentStatus =
  | "pending"
  | "downloaded"
  | "verified"
  | "failed";

// ---------------------------------------------------------------------------
// Schema types — must match backend Pydantic models field-for-field
// ---------------------------------------------------------------------------

export type MiniTortClaimFlagSchema = {
  id: string;
  created_at: string;
  claim_id: string;
  flag_type: string;
  severity: MiniTortFlagSeverity;
  source: string;
  message: string;
  resolved: boolean;
  resolved_by_id?: string | null;
  resolved_at?: string | null;
  resolved_reason?: string | null;
  attachment_id?: string | null;
};

export type MiniTortClaimSchema = {
  id: string;
  created_at: string;
  modified_at: string;
  matter_id: string;
  stage: MiniTortStage;
  stage_entered_at: string;
  sms_consent: boolean;
  email_consent: boolean;
  preferred_contact_method?: ContactMethod;
  // 1P coverage
  coverage_1p_id?: string | null;
  coverage_1p_status?: PrerequisiteStatus;
  coverage_1p_satisfied_at?: string | null;
  coverage_1p_source?: string | null;
  coverage_1p_confidence?: number | null;
  // 3P coverage
  coverage_3p_id?: string | null;
  coverage_3p_status?: PrerequisiteStatus;
  coverage_3p_satisfied_at?: string | null;
  coverage_3p_source?: string | null;
  coverage_3p_confidence?: number | null;
  // Police report
  police_report_id?: string | null;
  police_report_status?: PrerequisiteStatus;
  police_report_satisfied_at?: string | null;
  police_report_source?: string | null;
  // Processing
  processing_status?: PrerequisiteStatus;
  processing_completed_at?: string | null;
  // Review
  review_status?: PrerequisiteStatus;
  review_approved_at?: string | null;
  review_approved_by_id?: string | null;
  // Damage
  damage_amount?: string | null;
  damage_description?: string | null;
  missing_item_prompt?: string | null;
  // Rejection
  rejection_reason?: string | null;
  // Injury
  injury_detected: boolean;
  injury_detected_at?: string | null;
  // Cross-firm intake linkage
  converted_intake_id?: string | null;
};

export type MiniTortClaimListSchema = {
  results: MiniTortClaimSchema[];
  count: number;
};

export type ChaseProgressSchema = {
  current_step: number;
  total_steps: number;
  last_step_at?: string | null;
  next_step_at?: string | null;
};

export type MiniTortClaimDetailSchema = MiniTortClaimSchema & {
  demand_letter_status?: string | null;
  chase_progress?: ChaseProgressSchema | null;
  submission?: MiniTortSubmissionSchema | null;
};

export type MiniTortPipelineStatus = {
  claim_id: string;
  stage: MiniTortStage;
  prerequisites: {
    processing_status: PrerequisiteStatus;
    review_status: PrerequisiteStatus;
    coverage_1p_status: PrerequisiteStatus;
    coverage_3p_status: PrerequisiteStatus;
    police_report_status: PrerequisiteStatus;
  };
  has_unresolved_blockers: boolean;
  blocker_count: number;
  warning_count: number;
  chase_progress?: {
    claim_id: string;
    current_step_index: number;
    total_steps: number;
    stopped: boolean;
    completed: boolean;
  } | null;
  auto_close_deadline_at?: string | null;
  auto_close_on_hold: boolean;
  converted_intake_id?: string | null;
};

export type MiniTortSubmissionAttachmentSchema = {
  id: string;
  created_at: string;
  submission_id: string;
  attachment_type: MiniTortAttachmentType;
  status: MiniTortAttachmentStatus;
  source_url: string;
  blob_id?: string | null;
  original_filename?: string | null;
  content_type?: string | null;
  file_size_bytes?: number | null;
  error_message?: string | null;
};

export type MiniTortSubmissionSchema = {
  id: string;
  created_at: string;
  claim_id?: string | null;
  webhook_event_id?: string | null;
  raw_payload: { [key: string]: any };
  claimant_first_name: string;
  claimant_last_name: string;
  claimant_email?: string | null;
  claimant_phone?: string | null;
  preferred_contact_method?: ContactMethod;
  at_fault?: FaultStatus;
  deductible_paid?: DeductibleStatus;
  is_registered_owner_and_named_insured?: boolean;
  accident_within_last_year?: boolean;
  accident_date?: string | null;
  accident_location?: string | null;
  report_number?: string | null;
  police_department?: string | null;
  drivers_license_number?: string | null;
  date_of_birth?: string | null;
  authorized_mal_to_obtain?: boolean | null;
  was_injured?: boolean | null;
  wants_attorney_consult?: boolean | null;
  submitted_at?: string | null;
  source?: string;
  form_version?: string;
  external_submission_id: string;
  attachments?: MiniTortSubmissionAttachmentSchema[];
};

export type MiniTortClaimUpdateSchema = {
  sms_consent?: boolean | null;
  email_consent?: boolean | null;
  preferred_contact_method?: ContactMethod;
  damage_amount?: string | null;
  damage_description?: string | null;
  injury_detected?: boolean | null;
};

export type MiniTortTimelineEntry = {
  event_type: string;
  timestamp: string;
  old_stage?: MiniTortStage | null;
  new_stage?: MiniTortStage | null;
  description: string;
  metadata?: { [key: string]: string };
};

export type MiniTortStageTransitionRequest = {
  new_stage: MiniTortStage;
  missing_item_prompt?: string | null;
  rejection_reason?: string | null;
};

export const {
  useMiniTortClaimListQuery,
  useMiniTortClaimRetrieveQuery,
  useMiniTortClaimPartialUpdateMutation,
  useMiniTortClaimAutoCloseHoldMutation,
  useMiniTortClaimAutoCloseResumeMutation,
  useMiniTortClaimTimelineQuery,
  useMiniTortClaimTransitionMutation,
  useMiniTortClaimFlagsQuery,
  useMiniTortClaimFlagResolveMutation,
  useMiniTortClaimStatusQuery,
} = injectedRtkApi;
